import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { PROPOSAL_SET_VALUES } from "../../store/constants/proposalConstants";
import { Button, ButtonGroup } from "react-bootstrap";
import { FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaListUl, FaListOl, FaUndo, FaRedo, FaRobot } from 'react-icons/fa';
import CustomPromptModal from '../ProposalPreviewForm/CustomPromptModal';
import { aiTemplate } from "../../store/actions/proposals/proposals.actions";

const SimpleRichTextEditor = () => {
  const proposalValues = useSelector((state) => state.proposalValues);
  const [editorContent, setEditorContent] = useState(proposalValues?.user_scope || proposalValues?.scope);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const dispatch = useDispatch();

  const editorRef = useRef(null);
  const [selection, setSelection] = useState(null);

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      setSelection(selection.getRangeAt(0));
    }
  };

  const restoreSelection = () => {
    if (selection) {
      const newSelection = window.getSelection();
      newSelection.removeAllRanges();
      newSelection.addRange(selection);
    }
  };

  const checkUndoRedo = (ref, setUndo, setRedo) => {
    if (ref.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (ref.current.contains(range.commonAncestorContainer)) {
          setUndo(document.queryCommandEnabled('undo'));
          setRedo(document.queryCommandEnabled('redo'));
        }
      }
    }
  };

  const handleEditorChange = (event) => {
    if (editorRef.current) {
      const newContent = event.target.innerHTML;
      setEditorContent(newContent);
      dispatch({
        type: PROPOSAL_SET_VALUES,
        payload: { user_scope: newContent }
      });
      checkUndoRedo(editorRef, setCanUndo, setCanRedo);
    }
  };

  const handleFormat = (command, value = null) => (e) => {
    e.preventDefault();
    saveSelection();
    document.execCommand(command, false, value);
    restoreSelection();
    editorRef.current?.focus();
  };

  const handleUndo = (e) => {
    e.preventDefault();
    if (editorRef.current) {
      editorRef.current.focus();
      saveSelection();
      document.execCommand('undo', false, null);
      restoreSelection();
      checkUndoRedo(editorRef, setCanUndo, setCanRedo);
    }
  };

  const handleRedo = (e) => {
    e.preventDefault();
    if (editorRef.current) {
      editorRef.current.focus();
      saveSelection();
      document.execCommand('redo', false, null);
      restoreSelection();
      checkUndoRedo(editorRef, setCanUndo, setCanRedo);
    }
  };

  const handleEditorFocus = () => {
    if (editorRef.current) {
      editorRef.current.focus();
      checkUndoRedo(editorRef, setCanUndo, setCanRedo);
    }
  };

  useEffect(() => {
    setEditorContent(proposalValues?.user_scope || proposalValues?.scope);
  }, [proposalValues?.proposal_template, proposalValues?.scope, proposalValues?.user_scope]);

  return (
    <div className="editor-container">
      <div className="rich-text-toolbar mb-3 d-flex align-items-center">
        <div className="d-flex">
          <ButtonGroup className="me-2">
            <Button variant="light" onMouseDown={handleFormat('bold')} title="Bold">
              <FaBold />
            </Button>
            <Button variant="light" onMouseDown={handleFormat('italic')} title="Italic">
              <FaItalic />
            </Button>
            <Button variant="light" onMouseDown={handleFormat('underline')} title="Underline">
              <FaUnderline />
            </Button>
          </ButtonGroup>

          <ButtonGroup className="me-2">
            <Button variant="light" onMouseDown={handleFormat('justifyLeft')} title="Align Left">
              <FaAlignLeft />
            </Button>
            <Button variant="light" onMouseDown={handleFormat('justifyCenter')} title="Align Center">
              <FaAlignCenter />
            </Button>
            <Button variant="light" onMouseDown={handleFormat('justifyRight')} title="Align Right">
              <FaAlignRight />
            </Button>
          </ButtonGroup>

          <ButtonGroup className="me-2">
            <Button variant="light" onMouseDown={handleFormat('insertUnorderedList')} title="Bullet List">
              <FaListUl />
            </Button>
            <Button variant="light" onMouseDown={handleFormat('insertOrderedList')} title="Numbered List">
              <FaListOl />
            </Button>
          </ButtonGroup>

          <ButtonGroup>
            <Button
              variant="light"
              onMouseDown={handleUndo}
              title="Undo"
              disabled={!canUndo}
            >
              <FaUndo />
            </Button>
            <Button
              variant="light"
              onMouseDown={handleRedo}
              title="Redo"
              disabled={!canRedo}
            >
              <FaRedo />
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <div
        ref={editorRef}
        style={{ textTransform: "initial", fontSize: "15px", fontFamily: 'Arial' }}
        className="editor"
        contentEditable={true}
        dangerouslySetInnerHTML={{ __html: editorContent }}
        onBlur={handleEditorChange}
        onFocus={handleEditorFocus}
        onInput={handleEditorFocus}
      ></div>
    </div>
  );
};

export default SimpleRichTextEditor;
